import { type DragEvent, useCallback, useEffect, useRef } from "react";

type FileDnDWrapperProps = {
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
  onError?: (message: string) => void;
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
  disabled?: boolean;
  handleSelectedFiles: (e: DragEvent<HTMLDivElement>) => void;
};

const toReactDragEvent = (e: globalThis.DragEvent) =>
  e as unknown as DragEvent<HTMLDivElement>;

export const FileDnDWrapper = (props: FileDnDWrapperProps) => {
  const {
    onDragOver = () => {},
    onDragLeave = () => {},
    children,
    containerStyle,
    disabled,
    handleSelectedFiles,
  } = props;
  const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const handleDragEvent = useCallback((e: globalThis.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(
    (e: globalThis.DragEvent) => {
      if (disabled) return;
      handleDragEvent(e);
      onDragOver(toReactDragEvent(e));
    },
    [onDragOver, disabled, handleDragEvent],
  );

  const handleDragOver = useCallback(
    (e: globalThis.DragEvent) => {
      if (leaveTimeout.current) {
        clearTimeout(leaveTimeout.current);
        leaveTimeout.current = null;
      }
      if (disabled) return;
      handleDragEvent(e);
      onDragOver(toReactDragEvent(e));
    },
    [onDragOver, disabled, handleDragEvent],
  );

  const handleDragLeave = useCallback(
    (e: globalThis.DragEvent) => {
      if (disabled) return;
      handleDragEvent(e);
      if (leaveTimeout.current) {
        clearTimeout(leaveTimeout.current);
      }
      leaveTimeout.current = setTimeout(() => {
        onDragLeave(toReactDragEvent(e));
      }, 150);
    },
    [onDragLeave, disabled, handleDragEvent],
  );

  const handleDrop = useCallback(
    (e: globalThis.DragEvent) => {
      if (disabled) return;
      handleDragEvent(e);
      handleSelectedFiles(toReactDragEvent(e));
      onDragLeave(toReactDragEvent(e));
    },
    [disabled, handleSelectedFiles, onDragLeave, handleDragEvent],
  );

  useEffect(() => {
    const dropContainer = dropRef.current;
    if (!dropContainer) return;

    dropContainer.addEventListener("dragenter", handleDragEnter);
    dropContainer.addEventListener("dragleave", handleDragLeave);
    dropContainer.addEventListener("dragover", handleDragOver);
    dropContainer.addEventListener("drop", handleDrop);
    return () => {
      dropContainer.removeEventListener("dragenter", handleDragEnter);
      dropContainer.removeEventListener("dragleave", handleDragLeave);
      dropContainer.removeEventListener("dragover", handleDragOver);
      dropContainer.removeEventListener("drop", handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <div ref={dropRef} style={containerStyle}>
      {children}
    </div>
  );
};
